import { useState, useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@pantry/ui';
import { useCreateProductGroup } from '@pantry/api-client';
import { PlusIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

interface NewGroupDialogProps {
  householdId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CATEGORIES = [
  'Dairy',
  'Produce',
  'Meat & Seafood',
  'Bakery',
  'Pantry',
  'Frozen',
  'Beverages',
  'Snacks',
  'Condiments',
  'Other',
];

/**
 * Dialog for creating a new custom product group.
 */
export function NewGroupDialog({ householdId, open, onOpenChange }: NewGroupDialogProps) {
  const { t } = useLingui();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createGroup = useCreateProductGroup();

  useEffect(() => {
    if (open) {
      setName('');
      setCategory('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t`Please enter a name for this group`);
      return;
    }

    try {
      await createGroup.mutateAsync({
        householdId,
        data: { name: name.trim(), category: category || undefined },
      });
      toast.success(t`Group "${name.trim()}" created`);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to create group`;
      setError(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <PlusIcon className="h-5 w-5 text-primary" />
            </div>
            <Trans context="dialog_title">New Group</Trans>
          </DialogTitle>
          <DialogDescription className="pt-1">
            <Trans>Create a custom product group for your household.</Trans>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="group-name">
              <Trans>Name</Trans>
            </Label>
            <Input
              id="group-name"
              type="text"
              placeholder={t`e.g., Breakfast Items`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-xl"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="group-category">
              <Trans>Category (optional)</Trans>
            </Label>
            <select
              id="group-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="flex h-10 w-full rounded-xl border border-border/50 bg-muted/30 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all cursor-pointer hover:border-border"
            >
              <option value="">{t`No category`}</option>
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2">
              {error}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createGroup.isPending}
              className="rounded-xl"
            >
              <Trans context="action_button">Cancel</Trans>
            </Button>
            <Button type="submit" disabled={createGroup.isPending} className="rounded-xl">
              {createGroup.isPending ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" />
                  <Trans>Creating...</Trans>
                </div>
              ) : (
                <Trans context="action_button">Create Group</Trans>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
